<template>
    <AppModal ref="filtersModal" :title="translate('labels.table_filters')">
        <div class="modal-body">
           <table class="table table-bordered table-fixed">
                <thead>
                    <tr>
                        <th width="50%">{{ $t('labels.field') }}</th>
                        <th width="50%">{{ $t('labels.action') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="field in fields">
                        <tr>
                            <td>{{ field.label }}</td>
                            <td>
                                <AppSelect
                                    :options="getActionOptionsByField(field.key)"
                                    :model-value="field.selectedAction?.key"
                                    :selected-value="field.selectedAction?.label"
                                    @update:model-value="selectAction(field, $event)"
                                    option-key="key"
                                    option-value="label"
                                    can-be-empty
                                />
                            </td>
                        </tr>
                        <tr v-if="field.selectedAction">
                            <td v-if="field.key === 'status_uuids'" colspan="2">
                                <AppGroupSelect
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    :groups="statusGroups"
                                />
                            </td>
                            <td v-else-if="field.key === 'delivery_status_types'" colspan="2">
                                <AppMultiselect
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    :options="deliveryStatuses"
                                    option-key="key"
                                    option-value="label"
                                    can-be-empty
                                />
                            </td>
                            <td v-else-if="field.key === 'source_order_status_types'" colspan="2">
                                <AppMultiselect
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    :options="sourceOrderStatusTypes"
                                    option-key="key"
                                    option-value="label"
                                    can-be-empty
                                />
                            </td>
                            <td v-else-if="field.key === 'source_uuids'" colspan="2">
                                <MultiAutoComplete
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    :url="field.url"
                                />
                            </td>
                            <template v-else-if="['created_at', 'closed_at'].includes(field.key)">
                                <td v-if="field.selectedAction.key === 'from'" colspan="2">
                                    <DatePicker
                                        v-model="field.value.from"
                                    />
                                </td>
                                <td v-else-if="field.selectedAction.key === 'to'" colspan="2">
                                    <DatePicker
                                        v-model="field.value.to"
                                    />
                                </td>
                                <td colspan="2" v-else-if="field.selectedAction.key === 'range'">
                                    <div class="row align-items-center">
                                        <div class="col-lg-5">
                                            <DatePicker
                                                v-model="field.value.from"
                                            />
                                        </div>
                                        <div class="col-lg-2 text-center">
                                            {{ $t('labels.to') }}
                                        </div>
                                        <div class="col-lg-5">
                                            <DatePicker
                                                v-model="field.value.to"
                                            />
                                        </div>
                                    </div>
                                </td>
                            </template>
                            <td v-if="field.key === 'deliveries' && field.selectedAction.key === 'correlate'" colspan="2">
                                <AppMultiselect
                                    :options="deliveryOptions"
                                    v-model="field.value"
                                    option-key="key"
                                    option-value="label"
                                />
                            </td>
                            <td v-else-if="field.key === 'products' && field.selectedAction.key === 'correlate'" colspan="2">
                                <MultiAutoComplete
                                    :model-value="field.value ?? []"
                                    @update:model-value="field.value = $event"
                                    url="/catalog/product-items"
                                />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <div class="modal-footer">
            <AppButton size="sm" @click="apply" color="teal">{{ translate('buttons.apply') }}</AppButton>
        </div>
    </AppModal>
</template>
<script>
import AppSelect from '../../../../../components/new/form/select/AppSelect.vue';
import MultiAutoComplete from "../../../../../components/new/form/autocomplete/MultiAutoComplete.vue";
import AppGroupSelect from '../../../../components/select/AppGroupSelect.vue';
import { httpClient } from '../../../../../services/HttpClient';
import AppModal from "../../../../../components/new/modal/AppModal.vue";
import AppButton from "../../../../../components/new/button/AppButton.vue";
import DatePicker from '../../../../../components/new/form/datePiker/DatePicker.vue';
import {DeliveryIntegrationsEnum, showDeliveryIntegration} from "../../../../enums/DeliveryIntegrationsEnum";
import AppMultiselect from "../../../../../components/new/form/autocomplete/AppMultiselect.vue";
import FormGroup from '../../../../../components/new/form/group/FormGroup.vue';

import { DeliveryStatusEnum } from "../../../../enums/DeliveryStatusEnum";

export default {
    name: "OrdersFiltersModal",
    components: {
        AppSelect, MultiAutoComplete, AppModal, AppButton, DatePicker, AppMultiselect, FormGroup, AppGroupSelect
    },
    emits: ['update:filters'],
    data() {
        return {
            DeliveryStatusEnum,
            DeliveryIntegrationsEnum,
            pendingRestore: null,
            fields: [
                { 
                    key: 'status_uuids', 
                    label: this.$t('labels.order_statuses'), 
                    selectedAction: null, 
                    value: null,
                    url: `/projects/${this.$route.params.projectUuid}/crm/statuses`
                },
                { 
                    key: 'source_uuids', 
                    label: this.$t('labels.order_sources'), 
                    selectedAction: null, 
                    value: null,
                    url: `/projects/${this.$route.params.projectUuid}/crm/sources`
                },
                { 
                    key: 'created_at', 
                    label: this.$t('table.titles.created_at'), 
                    selectedAction: null, 
                    value: null 
                },
                { 
                    key: 'closed_at', 
                    label: this.$t('table.titles.closed_at'), 
                    selectedAction: null, 
                    value: null 
                },
                { 
                    key: 'products', 
                    label: this.$t('labels.products'), 
                    selectedAction: null, 
                    value: null 
                },
                { 
                    key: 'exit_declarations', 
                    label: this.$t('labels.exit_declarations'), 
                    selectedAction: null, 
                    value: null 
                },
                { 
                    key: 'company_number_in_deliveries', 
                    label: this.$t('labels.ttn_in_delivery'), 
                    selectedAction: null, 
                    value: null 
                },
                { 
                    key: 'delivery_status_types', 
                    label: this.$t('labels.delivery_status_types'), 
                    selectedAction: null, 
                    value: null
                },
                {
                    key: 'deliveries',
                    label: this.$t('labels.delivery'),
                    selectedAction: null,
                    value: null
                },
                {
                    key: 'sticker_file_in_deliveries',
                    label: this.$t('labels.sticker_file'),
                    selectedAction: null,
                    value: null
                },
                {
                    key: 'waybill_file_in_deliveries',
                    label: this.$t('labels.delivery_invoice'),
                    selectedAction: null,
                    value: null
                },
                {
                    key: 'source_order_delivery_number_exists',
                    label: this.$t('labels.source_order_delivery_number_exists'),
                    selectedAction: null,
                    value: null
                },
                {
                    key: 'source_order_status_types',
                    label: this.$t('labels.source_order_status_types'),
                    selectedAction: null,
                    value: null
                },
            ],
            deliveryOptions: Object.values(DeliveryIntegrationsEnum).map(value => ({
                key: value,
                label: showDeliveryIntegration(value)
            })),
            statusGroups: [],
            deliveryStatuses: Object.values(DeliveryStatusEnum).map(value => ({
                key: value,
                label: this.$t(`delivery_status.${value}`)
            })),
            sourceOrderStatusTypes: ['waiting', 'approved', 'declined', 'completed'].map(value => ({
                key: value,
                label: this.$t(`source_order_status.${value}`)
            })),
        }
    },
    methods: {
        show() {
            this.$refs.filtersModal.show();
        },
        getFieldsState() {
            return this.fields
                .filter(field => field.selectedAction !== null)
                .map(field => ({ key: field.key, selectedAction: field.selectedAction, value: field.value }));
        },
        queueRestore(filters) {
            this.pendingRestore = filters;
        },
        restoreFromFilters(filters) {
            const source = filters ?? {};
            const correlate = { key: 'correlate', label: this.$t('labels.correlate') };
            const present = { key: 'present', label: this.$t('labels.present') };
            const missing = { key: 'missing', label: this.$t('labels.missing') };
            const isTrue = value => Number(value) === 1;

            this.fields.forEach(field => {
                field.selectedAction = null;
                field.value = null;
            });

            const setField = (key, selectedAction, value) => {
                const field = this.fields.find(f => f.key === key);
                if (!field) return;
                field.selectedAction = selectedAction;
                field.value = value;
            };

            if (Array.isArray(source.status_uuids) && source.status_uuids.length > 0) {
                setField('status_uuids', correlate, source.status_uuids);
            }
            if (Array.isArray(source.source_uuids) && source.source_uuids.length > 0) {
                setField('source_uuids', correlate, source.source_uuids.map(uuid => ({ uuid })));
            }
            if (source.created_at_from || source.created_at_to) {
                if (source.created_at_from && source.created_at_to) {
                    setField('created_at', { key: 'range', label: this.$t('labels.range') }, { from: source.created_at_from, to: source.created_at_to });
                } else if (source.created_at_from) {
                    setField('created_at', { key: 'from', label: this.$t('labels.from') }, { from: source.created_at_from });
                } else {
                    setField('created_at', { key: 'to', label: this.$t('labels.to') }, { to: source.created_at_to });
                }
            }
            if (source.closed_at_from || source.closed_at_to) {
                if (source.closed_at_from && source.closed_at_to) {
                    setField('closed_at', { key: 'range', label: this.$t('labels.range') }, { from: source.closed_at_from, to: source.closed_at_to });
                } else if (source.closed_at_from) {
                    setField('closed_at', { key: 'from', label: this.$t('labels.from') }, { from: source.closed_at_from });
                } else {
                    setField('closed_at', { key: 'to', label: this.$t('labels.to') }, { to: source.closed_at_to });
                }
            }
            if (Array.isArray(source.products_uuids) && source.products_uuids.length > 0) {
                setField('products', correlate, source.products_uuids.map(uuid => ({ uuid })));
            } else if (source.has_product !== undefined) {
                setField('products', isTrue(source.has_product) ? present : missing, { has_product: isTrue(source.has_product) });
            }
            if (Array.isArray(source.delivery_methods) && source.delivery_methods.length > 0) {
                setField('deliveries', correlate, this.deliveryOptions.filter(option => source.delivery_methods.includes(option.key)));
            } else if (source.has_deliveries !== undefined) {
                setField('deliveries', isTrue(source.has_deliveries) ? present : missing, { has_deliveries: isTrue(source.has_deliveries) });
            }
            if (source.has_exit_declarations !== undefined) {
                setField('exit_declarations', isTrue(source.has_exit_declarations) ? present : missing, { has_exit_declarations: isTrue(source.has_exit_declarations) });
            }
            if (source.has_company_number_in_deliveries !== undefined) {
                setField('company_number_in_deliveries', isTrue(source.has_company_number_in_deliveries) ? present : missing, { has_company_number_in_deliveries: isTrue(source.has_company_number_in_deliveries) });
            }
            if (source.has_sticker_file_in_deliveries !== undefined) {
                setField('sticker_file_in_deliveries', isTrue(source.has_sticker_file_in_deliveries) ? present : missing, { has_sticker_file_in_deliveries: isTrue(source.has_sticker_file_in_deliveries) });
            }
            if (source.has_waybill_file_in_deliveries !== undefined) {
                setField('waybill_file_in_deliveries', isTrue(source.has_waybill_file_in_deliveries) ? present : missing, { has_waybill_file_in_deliveries: isTrue(source.has_waybill_file_in_deliveries) });
            }
            if (Array.isArray(source.delivery_status_types) && source.delivery_status_types.length > 0) {
                setField('delivery_status_types', correlate, this.deliveryStatuses.filter(option => source.delivery_status_types.includes(option.key)));
            }
            if (source.integration_order_delivery_number_exists !== undefined) {
                setField('source_order_delivery_number_exists', isTrue(source.integration_order_delivery_number_exists) ? present : missing, { source_order_delivery_number_exists: isTrue(source.integration_order_delivery_number_exists) });
            }
            if (Array.isArray(source.integration_order_status_types) && source.integration_order_status_types.length > 0) {
                setField('source_order_status_types', correlate, this.sourceOrderStatusTypes.filter(option => source.integration_order_status_types.includes(option.key)));
            }

            this.$emit('update:filters', this.getFilters(), this.fields.filter(f => f.selectedAction !== null).length, this.getFieldsState());

            this.hydrateAutocompleteLabels();
        },
        async hydrateAutocompleteLabels() {
            const sourceField = this.fields.find(f => f.key === 'source_uuids');
            if (sourceField?.selectedAction && Array.isArray(sourceField.value) && sourceField.value.length > 0) {
                const uuids = sourceField.value.map(item => item.uuid);
                await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/sources`, { uuids, limit: uuids.length })
                    .then(response => { sourceField.value = response.data.data; });
            }

            const productField = this.fields.find(f => f.key === 'products');
            if (productField?.selectedAction?.key === 'correlate' && Array.isArray(productField.value) && productField.value.length > 0) {
                const uuids = productField.value.map(item => item.uuid);
                await httpClient.get('/catalog/product-items', { uuids, limit: uuids.length })
                    .then(response => { productField.value = response.data.data; });
            }
        },
        clear() {
            this.fields.forEach(field => {
                field.selectedAction = null;
                field.value = null;
            });
            this.$emit('update:filters', {}, 0, []);
        },
        async loadStatuses() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/statuses`, {
                orders: [
                    {
                        column: 'priority_order',
                        dir: 'asc'
                    }
                ],
                limit: 100
            }).then((response) => {
                const statuses = response.data.data;
                const typeLabels = {
                    'active': this.$t('orderStatus.types.active'),
                    'done': this.$t('orderStatus.types.done'),
                    'canceled': this.$t('orderStatus.types.canceled'),
                };
                const groupsMap = {};
                statuses.forEach(status => {
                    if (!groupsMap[status.type]) {
                        groupsMap[status.type] = {
                            name: typeLabels[status.type] ?? status.type,
                            items: []
                        };
                    }
                    groupsMap[status.type].items.push(status);
                });
                this.statusGroups = Object.values(groupsMap);

                if (this.pendingRestore !== null) {
                    const filters = this.pendingRestore;
                    this.pendingRestore = null;
                    this.restoreFromFilters(filters);
                    return;
                }

                const statusUuidsField = this.fields.find(f => f.key === 'status_uuids');
                if (statusUuidsField) {
                    statusUuidsField.selectedAction = { key: 'correlate', label: this.$t('labels.correlate') };
                    statusUuidsField.value = statuses
                        .filter(s => s.type === 'active')
                        .map(s => s.uuid);
                }

                this.$emit('update:filters', this.getFilters(), this.fields.filter(field => field.selectedAction !== null).length, this.getFieldsState());
            });
        },
        getActionOptionsByField(fieldKey) {
            if (['created_at', 'closed_at'].includes(fieldKey)) {
                return [
                    { key: 'from', label: this.$t('labels.from') },
                    { key: 'to', label: this.$t('labels.to') },
                    { key: 'range', label: this.$t('labels.range') },
                ];
            }
            if (['deliveries', 'products'].includes(fieldKey)) {
                return [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                    { key: 'correlate', label: this.$t('labels.correlate') },
                ];
            }
            if (fieldKey === 'exit_declarations' || fieldKey === 'company_number_in_deliveries' || fieldKey === 'sticker_file_in_deliveries' || fieldKey === 'waybill_file_in_deliveries' || fieldKey === 'source_order_delivery_number_exists') {
                return [
                    { key: 'present', label: this.$t('labels.present') },
                    { key: 'missing', label: this.$t('labels.missing') },
                ];
            }
            return [
                { key: 'correlate', label: this.$t('labels.correlate') },
            ];
        },
        selectAction(field, action) {
            if (['created_at', 'closed_at'].includes(field.key)) {
                if (action?.key === 'from') {
                    field.value = { from: null };
                } else if (action?.key === 'to') {
                    field.value = { to: null };
                } else if (action?.key === 'range') {
                    field.value = { from: null, to: null };
                }
            } else if (field.key === 'deliveries') {
                if (action?.key === 'present') {
                    field.value = { has_deliveries: true };
                } else if (action?.key === 'missing') {
                    field.value = { has_deliveries: false };
                } else if (action?.key === 'correlate') {
                    field.value = [];
                }
            } else if (field.key === 'products') {
                if (action?.key === 'present') {
                    field.value = { has_product: true };
                } else if (action?.key === 'missing') {
                    field.value = { has_product: false };
                } else if (action?.key === 'correlate') {
                    field.value = [];
                }
            } else if (field.key === 'exit_declarations') {
                if (action?.key === 'present') {
                    field.value = { has_exit_declarations: true };
                } else if (action?.key === 'missing') {
                    field.value = { has_exit_declarations: false };
                }
            } else if (field.key === 'company_number_in_deliveries') {
                if (action?.key === 'present') {
                    field.value = { has_company_number_in_deliveries: true };
                } else if (action?.key === 'missing') {
                    field.value = { has_company_number_in_deliveries: false };
                }
            } else if (field.key === 'sticker_file_in_deliveries') {
                if (action?.key === 'present') {
                    field.value = { has_sticker_file_in_deliveries: true };
                } else if (action?.key === 'missing') {
                    field.value = { has_sticker_file_in_deliveries: false };
                }
            } else if (field.key === 'waybill_file_in_deliveries') {
                if (action?.key === 'present') {
                    field.value = { has_waybill_file_in_deliveries: true };
                } else if (action?.key === 'missing') {
                    field.value = { has_waybill_file_in_deliveries: false };
                }
            } else if (field.key === 'source_order_delivery_number_exists') {
                if (action?.key === 'present') {
                    field.value = { source_order_delivery_number_exists: true };
                } else if (action?.key === 'missing') {
                    field.value = { source_order_delivery_number_exists: false };
                }
            } else if (field.key === 'status_uuids') {
                field.value = [];
            } else if (field.key === 'delivery_status_types' || field.key === 'source_order_status_types') {
                field.value = [];
            } else {
                field.value = null;
            }
            field.selectedAction = action;
        },
        apply() {
            this.$emit('update:filters', this.getFilters(), this.fields.filter(field => field.selectedAction !== null).length, this.getFieldsState())
            this.$refs.filtersModal.hide()
        },
        getFilters() {
            const filters = {};
            this.fields.forEach(field => {
                if (!field.selectedAction || !field.value) return;

                if (field.key === 'created_at') {
                    if (field.selectedAction.key === 'from' && field.value.from) {
                        filters.created_at_from = field.value.from;
                    } else if (field.selectedAction.key === 'to' && field.value.to) {
                        filters.created_at_to = field.value.to;
                    } else if (field.selectedAction.key === 'range') {
                        if (field.value.from) filters.created_at_from = field.value.from;
                        if (field.value.to) filters.created_at_to = field.value.to;
                    }
                } else if (field.key === 'closed_at') {
                    if (field.selectedAction.key === 'from' && field.value.from) {
                        filters.closed_at_from = field.value.from;
                    } else if (field.selectedAction.key === 'to' && field.value.to) {
                        filters.closed_at_to = field.value.to;
                    } else if (field.selectedAction.key === 'range') {
                        if (field.value.from) filters.closed_at_from = field.value.from;
                        if (field.value.to) filters.closed_at_to = field.value.to;
                    }
                } else if (field.key === 'deliveries') {
                     if (field.selectedAction.key === 'present' || field.selectedAction.key === 'missing') {
                         filters.has_deliveries = field.value.has_deliveries;
                     } else if (field.selectedAction.key === 'correlate' && field.value && field.value.length > 0) {
                         filters.delivery_methods = field.value.map(option => option.key);
                     }
                } else if (field.key === 'products') {
                    if (field.selectedAction.key === 'present' || field.selectedAction.key === 'missing') {
                        filters.has_product = field.value.has_product;
                    } else if (field.selectedAction.key === 'correlate' && field.value && field.value.length > 0) {
                        filters.products_uuids = field.value.map(item => item.uuid);
                    }
                } else if (field.key === 'exit_declarations') {
                     if (field.selectedAction.key === 'present' || field.selectedAction.key === 'missing') {
                        filters.has_exit_declarations = field.value.has_exit_declarations;
                    } 
                } else if (field.key === 'company_number_in_deliveries') {
                     if (field.selectedAction.key === 'present' || field.selectedAction.key === 'missing') {
                        filters.has_company_number_in_deliveries = field.value.has_company_number_in_deliveries;
                    }
                } else if (field.key === 'sticker_file_in_deliveries') {
                    if (field.selectedAction.key === 'present' || field.selectedAction.key === 'missing') {
                        filters.has_sticker_file_in_deliveries = field.value.has_sticker_file_in_deliveries;
                    }
                } else if (field.key === 'waybill_file_in_deliveries') {
                    if (field.selectedAction.key === 'present' || field.selectedAction.key === 'missing') {
                        filters.has_waybill_file_in_deliveries = field.value.has_waybill_file_in_deliveries;
                    }
                } else if (field.key === 'status_uuids') {
                    if (field.value && field.value.length > 0) {
                        filters[field.key] = field.value;
                    }
                } else if (field.key === 'delivery_status_types') {
                    if (field.value && field.value.length > 0) {
                        filters[field.key] = field.value.map(item => item.key);
                    }
                } else if (field.key === 'source_order_delivery_number_exists') {
                    if (field.selectedAction.key === 'present' || field.selectedAction.key === 'missing') {
                        filters.integration_order_delivery_number_exists = field.value.source_order_delivery_number_exists;
                    }
                } else if (field.key === 'source_order_status_types') {
                    if (field.value && field.value.length > 0) {
                        filters.integration_order_status_types = field.value.map(item => item.key);
                    }
                } else {
                    filters[field.key] = field.value.map(item => item.uuid);
                }
            });
            return filters;
        }
    },
    async mounted() {
        await this.loadStatuses();
    },
}
</script>
